// ============================================================================
// FSA DEPRECIATION CALCULATION ENGINE  (ADR-0006)
// ============================================================================
// Pure computation over the tab's editable draft. The UI computes on every
// edit and the results are persisted verbatim on Save (save-compute-display-
// read) — the backend never re-derives, and print/Excel/suggestions read the
// saved figures.
//
// Working-paper conventions (Phase 1):
//   • Book (WDV): dep = max(0, opening − Σ disposal NBV) × rate
//                       + Σ addition × rate × pro-rata.
//     Disposals are treated as removed at the start of the year for the
//     depreciation base (dep up to the disposal date is deliberately not
//     auto-charged — ADR-0006 §3; the auditor adjusts if material).
//   • IT (s.32 block): dep = rate × max(0, opening + add≥180 − proceeds)
//                            + rate/2 × max(0, add<180 − spillover)
//     where spillover = proceeds not absorbed by the full-rate pool.
//   • Per-addition IT dep is INDICATIVE only — the block figure is
//     authoritative (proceeds absorption happens at block level).

import type {
  BookProrataConvention,
  DepAddition,
  DepCalcContext,
  DepCalcWorkspace,
  DepClassConfig,
  DepDisposal,
  DepItBlock,
  SaveDepCalcRequest,
} from 'api/types/fsa/depreciationCalc.types';
import type { ID } from 'api/types';

// ---------------------------------------------------------------------------
// Draft model — the tab's editable working state
// ---------------------------------------------------------------------------

export interface DraftAddition {
  localId: string;
  scheduleId: ID;
  scheduleClassId: ID | null;
  customClassLocalId: ID | null;
  classCode: string;
  /** Depreciation-internal sub-class key (rev 6); null = sits directly on the class. */
  subClassLocalId: ID | null;
  ledgerName: string | null;
  description: string;
  additionDate: string;      // YYYY-MM-DD
  putToUseDate: string;      // YYYY-MM-DD
  amount: number;
  itBlockEnumId: ID | null;
  itRateOverride: number | null;
  displayOrder: number;
  // computed
  daysUsed: number | null;
  isLt180Days: boolean | null;
  bookDepActual: number | null;
  bookDepRounded: number | null;
  itDepIndicativeActual: number | null;
  itDepIndicativeRounded: number | null;
}

export interface DraftDisposal {
  localId: string;
  scheduleId: ID;
  scheduleClassId: ID | null;
  customClassLocalId: ID | null;
  classCode: string;
  /** Depreciation-internal sub-class key (rev 6); null = sits directly on the class. */
  subClassLocalId: ID | null;
  ledgerName: string | null;
  description: string;
  originalCost: number;
  accumulatedDepreciation: number;
  disposalDate: string;      // YYYY-MM-DD
  disposalProceeds: number;
  itBlockEnumId: ID | null;
  displayOrder: number;
  // computed
  netBookValue: number | null;
  profitLossOnDisposal: number | null;
}

export interface DraftItBlock {
  itBlockEnumId: ID;
  enumKey: string | null;
  label: string | null;
  openingWdv: number;
  rate: number;
  /** User flag: block ceased with a positive balance → remainder is an IT loss. */
  isMarkedExhausted: boolean;
  // computed
  additionsGe180: number | null;
  additionsLt180: number | null;
  disposalProceeds: number | null;
  itDepActual: number | null;
  itDepRounded: number | null;
  closingWdvActual: number | null;
  closingWdvRounded: number | null;
  isBlockNegative: boolean;
  itProfitLoss: number | null;
}

export interface DepDraft {
  roundingPrecision: number;
  taxRate: number | null;
  bookProrataConvention: BookProrataConvention;
  dtOpeningPosition: number | null;
  dtClosingPositionActual: number | null;
  dtClosingPositionRounded: number | null;
  dtChargeForYearActual: number | null;
  dtChargeForYearRounded: number | null;
  classConfig: Record<string, DepClassConfig>;
  additions: DraftAddition[];
  disposals: DraftDisposal[];
  itBlocks: DraftItBlock[];
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

export const roundTo = (v: number, precision: number): number => {
  const f = Math.pow(10, precision);
  return Math.round((v + Number.EPSILON) * f) / f;
};

const dayMs = 86_400_000;

const toUtcDay = (iso: string): number => {
  const d = iso.slice(0, 10);
  return Date.parse(`${d}T00:00:00Z`);
};

/** Inclusive day span; NaN-safe (returns 0 for unparseable dates). */
const daysBetweenInclusive = (fromIso: string, toIso: string): number => {
  const a = toUtcDay(fromIso);
  const b = toUtcDay(toIso);
  if (Number.isNaN(a) || Number.isNaN(b)) return 0;
  return Math.round((b - a) / dayMs) + 1;
};

export const newLocalId = (): string =>
  (typeof crypto !== 'undefined' && 'randomUUID' in crypto)
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`;

/**
 * The classConfig key an entry aggregates under: its sub-class local id when
 * it sits on a sub-class, else its class code.
 */
export const entryConfigKey = (e: { classCode: string; subClassLocalId: ID | null }): string =>
  e.subClassLocalId != null ? String(e.subClassLocalId) : e.classCode;

// ---------------------------------------------------------------------------
// Draft construction
// ---------------------------------------------------------------------------

/**
 * Builds the editable draft from a loaded workspace. classConfig is merged
 * with the live class options so every fixed-asset class gets an entry.
 *
 * Opening WDV is NOT derived from the mapped ledgers' TB balances (rev 3 —
 * ledger mapping is optional and must never be the WDV basis): it comes
 * from the previous year's depreciation calc (roll-forward, Phase 1c) or
 * manual entry. New classes start at 0.
 */
export const buildDraft = (ws: DepCalcWorkspace): DepDraft => {
  const h = ws.header;
  const classConfig: Record<string, DepClassConfig> = {};

  for (const opt of ws.context.classOptions) {
    const existing = h.classConfig[opt.classCode];
    classConfig[opt.classCode] = existing
      ? { ...existing, schedule_code: opt.scheduleCode }
      : {
          schedule_code: opt.scheduleCode,
          book_rate: null,
          opening_wdv: 0,
        };
  }
  // Keep saved entries whose class no longer appears in the options
  // (declassified / removed custom class) — never silently drop data.
  for (const [code, cfg] of Object.entries(h.classConfig)) {
    if (!classConfig[code]) classConfig[code] = { ...cfg };
  }

  return {
    roundingPrecision: h.roundingPrecision,
    taxRate: h.taxRate,
    bookProrataConvention: h.bookProrataConvention,
    dtOpeningPosition: h.dtOpeningPosition,
    dtClosingPositionActual: h.dtClosingPositionActual,
    dtClosingPositionRounded: h.dtClosingPositionRounded,
    dtChargeForYearActual: h.dtChargeForYearActual,
    dtChargeForYearRounded: h.dtChargeForYearRounded,
    classConfig,
    additions: ws.additions.map(additionToDraft),
    disposals: ws.disposals.map(disposalToDraft),
    itBlocks: ws.itBlocks.map(blockToDraft),
  };
};

const additionToDraft = (a: DepAddition): DraftAddition => ({
  localId: newLocalId(),
  scheduleId: a.scheduleId,
  scheduleClassId: a.scheduleClassId,
  customClassLocalId: a.customClassLocalId,
  classCode: a.classCode ?? '',
  subClassLocalId: a.subClassLocalId,
  ledgerName: a.ledgerName,
  description: a.description,
  additionDate: a.additionDate.slice(0, 10),
  putToUseDate: a.putToUseDate.slice(0, 10),
  amount: a.amount,
  itBlockEnumId: a.itBlockEnumId,
  itRateOverride: a.itRateOverride,
  displayOrder: a.displayOrder,
  daysUsed: a.daysUsed,
  isLt180Days: a.isLt180Days,
  bookDepActual: a.bookDepActual,
  bookDepRounded: a.bookDepRounded,
  itDepIndicativeActual: a.itDepIndicativeActual,
  itDepIndicativeRounded: a.itDepIndicativeRounded,
});

const disposalToDraft = (d: DepDisposal): DraftDisposal => ({
  localId: newLocalId(),
  scheduleId: d.scheduleId,
  scheduleClassId: d.scheduleClassId,
  customClassLocalId: d.customClassLocalId,
  classCode: d.classCode ?? '',
  subClassLocalId: d.subClassLocalId,
  ledgerName: d.ledgerName,
  description: d.description,
  originalCost: d.originalCost,
  accumulatedDepreciation: d.accumulatedDepreciation,
  disposalDate: d.disposalDate.slice(0, 10),
  disposalProceeds: d.disposalProceeds,
  itBlockEnumId: d.itBlockEnumId,
  displayOrder: d.displayOrder,
  netBookValue: d.netBookValue,
  profitLossOnDisposal: d.profitLossOnDisposal,
});

const blockToDraft = (b: DepItBlock): DraftItBlock => ({
  itBlockEnumId: b.itBlockEnumId,
  enumKey: b.itBlockKey,
  label: b.itBlockLabel,
  openingWdv: b.openingWdv,
  rate: b.rate,
  isMarkedExhausted: b.isMarkedExhausted,
  additionsGe180: b.additionsGe180,
  additionsLt180: b.additionsLt180,
  disposalProceeds: b.disposalProceeds,
  itDepActual: b.itDepActual,
  itDepRounded: b.itDepRounded,
  closingWdvActual: b.closingWdvActual,
  closingWdvRounded: b.closingWdvRounded,
  isBlockNegative: b.isBlockNegative,
  itProfitLoss: b.itProfitLoss,
});

// ---------------------------------------------------------------------------
// Recompute — the single entry point after any edit
// ---------------------------------------------------------------------------

export const recomputeDraft = (draft: DepDraft, context: DepCalcContext): DepDraft => {
  const p = draft.roundingPrecision;

  // Ensure every live class option has a classConfig entry. Classes can
  // appear AFTER the draft was seeded (a custom class declared in the Notes
  // tab while this tab stayed mounted) — without this merge they would be
  // missing from the Book Schedule and their additions would aggregate
  // nowhere.
  const mergedClassConfig: Record<string, DepClassConfig> = { ...draft.classConfig };
  for (const opt of context.classOptions) {
    if (!mergedClassConfig[opt.classCode]) {
      mergedClassConfig[opt.classCode] = {
        schedule_code: opt.scheduleCode,
        book_rate: null,
        opening_wdv: 0,
      };
    }
  }
  draft = { ...draft, classConfig: mergedClassConfig };
  const fyStart = (context.periodStart ?? '').slice(0, 10);
  const fyEnd = context.financialYearEnd.slice(0, 10);
  const daysInFy = fyStart ? Math.max(1, daysBetweenInclusive(fyStart, fyEnd)) : 365;

  const blockRateByEnumId = new Map<string, number>(
    context.itBlockOptions.map(o => [String(o.id), o.rate]),
  );

  // ---- 1 · Per-addition computed values ------------------------------------
  const additions = draft.additions.map(a => {
    const rawDays = daysBetweenInclusive(a.putToUseDate, fyEnd);
    const daysUsed = Math.min(Math.max(rawDays, 0), daysInFy);
    const isLt180 = daysUsed < 180;

    // Sub-class entries take the sub-class's own rate; fall back to the
    // parent class rate if the sub entry vanished (defensive).
    const classRate = draft.classConfig[entryConfigKey(a)]?.book_rate
      ?? draft.classConfig[a.classCode]?.book_rate
      ?? 0;
    const prorata = draft.bookProrataConvention === 'HALF_RATE_180'
      ? (isLt180 ? 0.5 : 1)
      : daysUsed / daysInFy;
    const bookDep = a.amount * (classRate / 100) * prorata;

    const itRate = a.itRateOverride
      ?? (a.itBlockEnumId != null ? blockRateByEnumId.get(String(a.itBlockEnumId)) ?? 0 : 0);
    const itDep = a.amount * (itRate / 100) * (isLt180 ? 0.5 : 1);

    return {
      ...a,
      daysUsed,
      isLt180Days: isLt180,
      bookDepActual: bookDep,
      bookDepRounded: roundTo(bookDep, p),
      itDepIndicativeActual: itDep,
      itDepIndicativeRounded: roundTo(itDep, p),
    };
  });

  // ---- 2 · Per-disposal computed values ------------------------------------
  const disposals = draft.disposals.map(d => {
    const nbv = d.originalCost - d.accumulatedDepreciation;
    return {
      ...d,
      netBookValue: roundTo(nbv, p),
      profitLossOnDisposal: roundTo(d.disposalProceeds - nbv, p),
    };
  });

  // ---- 3 · Class-level book totals -----------------------------------------
  const classConfig: Record<string, DepClassConfig> = {};
  for (const [code, cfg] of Object.entries(draft.classConfig)) {
    // Entries aggregate under their config key: the sub-class local id when
    // they sit on a sub-class, else the class code. A parent class with
    // sub-classes therefore only aggregates its DIRECT entries — the sub
    // rows carry their own; note suggestions roll them up server-side.
    const classAdditions = additions.filter(a => entryConfigKey(a) === code);
    const classDisposals = disposals.filter(d => entryConfigKey(d) === code);

    const additionsTotal = classAdditions.reduce((s, a) => s + a.amount, 0);
    const disposalsNbv = classDisposals.reduce((s, d) => s + (d.netBookValue ?? 0), 0);
    const disposalsCost = classDisposals.reduce((s, d) => s + d.originalCost, 0);
    const disposalsAccum = classDisposals.reduce((s, d) => s + d.accumulatedDepreciation, 0);

    const opening = cfg.opening_wdv ?? 0;
    const rate = cfg.book_rate ?? 0;
    const openingDepBase = Math.max(0, opening - disposalsNbv);
    const depForYear = openingDepBase * (rate / 100)
      + classAdditions.reduce((s, a) => s + (a.bookDepActual ?? 0), 0);
    const closing = opening + additionsTotal - disposalsNbv - depForYear;

    classConfig[code] = {
      ...cfg,
      additions_total: roundTo(additionsTotal, p),
      disposals_nbv_total: roundTo(disposalsNbv, p),
      disposals_cost_total: roundTo(disposalsCost, p),
      disposals_accum_dep_total: roundTo(disposalsAccum, p),
      dep_for_year_actual: depForYear,
      dep_for_year_rounded: roundTo(depForYear, p),
      closing_wdv_actual: closing,
      closing_wdv_rounded: roundTo(closing, p),
    };
  }

  // ---- 4 · IT block schedule (authoritative s.32 mechanics) ----------------
  // Rows = existing blocks ∪ blocks referenced by any addition/disposal.
  const blockMap = new Map<string, DraftItBlock>();
  for (const b of draft.itBlocks) blockMap.set(String(b.itBlockEnumId), { ...b });
  const ensureBlock = (enumId: ID | null) => {
    if (enumId == null) return;
    const key = String(enumId);
    if (!blockMap.has(key)) {
      const opt = context.itBlockOptions.find(o => String(o.id) === key);
      blockMap.set(key, {
        itBlockEnumId: enumId,
        enumKey: opt?.enumKey ?? null,
        label: opt?.enumValue ?? null,
        openingWdv: 0,
        rate: opt?.rate ?? 0,
        isMarkedExhausted: false,
        additionsGe180: null,
        additionsLt180: null,
        disposalProceeds: null,
        itDepActual: null,
        itDepRounded: null,
        closingWdvActual: null,
        closingWdvRounded: null,
        isBlockNegative: false,
        itProfitLoss: null,
      });
    }
  };
  additions.forEach(a => ensureBlock(a.itBlockEnumId));
  disposals.forEach(d => ensureBlock(d.itBlockEnumId));

  const itBlocks = Array.from(blockMap.values()).map(b => {
    const key = String(b.itBlockEnumId);
    const ge180 = additions
      .filter(a => String(a.itBlockEnumId) === key && a.isLt180Days === false)
      .reduce((s, a) => s + a.amount, 0);
    const lt180 = additions
      .filter(a => String(a.itBlockEnumId) === key && a.isLt180Days === true)
      .reduce((s, a) => s + a.amount, 0);
    const proceeds = disposals
      .filter(d => String(d.itBlockEnumId) === key)
      .reduce((s, d) => s + d.disposalProceeds, 0);

    // Block cessation (s.50-equivalent treatment, rev 3):
    //   gross = opening + all additions − proceeds
    //   • gross < 0 → auto-exhausted: proceeds exceeded the block →
    //     IT PROFIT of −gross; no depreciation; closing 0.
    //   • gross ≥ 0 but the user marked the block exhausted (all assets
    //     disposed) → the remainder is an IT LOSS of −gross; no
    //     depreciation; closing 0.
    const gross = b.openingWdv + ge180 + lt180 - proceeds;
    const isNegative = gross < 0;
    const ceased = isNegative || b.isMarkedExhausted;

    let itDep = 0;
    let itProfitLoss: number | null = null;
    let closing = 0;
    if (ceased) {
      itProfitLoss = -gross; // positive = profit, negative = loss
    } else {
      const fullPoolRaw = b.openingWdv + ge180 - proceeds;
      const fullPool = Math.max(0, fullPoolRaw);
      // Proceeds not absorbed by the full-rate pool spill into the half-rate pool.
      const spillover = Math.max(0, -fullPoolRaw);
      const halfPool = Math.max(0, lt180 - spillover);
      itDep = fullPool * (b.rate / 100) + halfPool * (b.rate / 200);
      closing = gross - itDep;
    }

    return {
      ...b,
      additionsGe180: roundTo(ge180, p),
      additionsLt180: roundTo(lt180, p),
      disposalProceeds: roundTo(proceeds, p),
      itDepActual: itDep,
      itDepRounded: roundTo(itDep, p),
      closingWdvActual: closing,
      closingWdvRounded: roundTo(closing, p),
      isBlockNegative: isNegative,
      itProfitLoss: itProfitLoss == null ? null : roundTo(itProfitLoss, p),
    };
  });

  // ---- 5 · Deferred tax (depreciation timing difference) -------------------
  // Sign convention (rev 5, auditor presentation): POSITIVE = DTA,
  // NEGATIVE = DTL. Book WDV above the IT tax base is a taxable temporary
  // difference → DTL → negative position. The charge keeps its own
  // semantic: positive = charge to P&L (a DTL increase / DTA decrease).
  const bookClosing = Object.values(classConfig).reduce((s, c) => s + (c.closing_wdv_actual ?? 0), 0);
  const itClosing = itBlocks.reduce((s, b) => s + (b.closingWdvActual ?? 0), 0);
  const taxRate = draft.taxRate ?? 0;
  const dtClosing = (itClosing - bookClosing) * (taxRate / 100);
  const dtCharge = (draft.dtOpeningPosition ?? 0) - dtClosing;

  return {
    ...draft,
    additions,
    disposals,
    classConfig,
    itBlocks,
    dtClosingPositionActual: dtClosing,
    dtClosingPositionRounded: roundTo(dtClosing, p),
    dtChargeForYearActual: dtCharge,
    dtChargeForYearRounded: roundTo(dtCharge, p),
  };
};

// ---------------------------------------------------------------------------
// Live preview (addition modal) — same math as recomputeDraft step 1
// ---------------------------------------------------------------------------

export interface AdditionPreview {
  daysUsed: number;
  isLt180Days: boolean;
  bookDep: number;
  itDepIndicative: number;
}

export const previewAddition = (
  args: {
    putToUseDate: string;
    amount: number;
    classRate: number;
    itRate: number;
    convention: BookProrataConvention;
  },
  context: DepCalcContext,
): AdditionPreview => {
  const fyStart = (context.periodStart ?? '').slice(0, 10);
  const fyEnd = context.financialYearEnd.slice(0, 10);
  const daysInFy = fyStart ? Math.max(1, daysBetweenInclusive(fyStart, fyEnd)) : 365;
  const rawDays = args.putToUseDate ? daysBetweenInclusive(args.putToUseDate, fyEnd) : 0;
  const daysUsed = Math.min(Math.max(rawDays, 0), daysInFy);
  const isLt180Days = daysUsed < 180;
  const prorata = args.convention === 'HALF_RATE_180'
    ? (isLt180Days ? 0.5 : 1)
    : daysUsed / daysInFy;
  return {
    daysUsed,
    isLt180Days,
    bookDep: args.amount * (args.classRate / 100) * prorata,
    itDepIndicative: args.amount * (args.itRate / 100) * (isLt180Days ? 0.5 : 1),
  };
};

// ---------------------------------------------------------------------------
// Save mapping
// ---------------------------------------------------------------------------

export const draftToSaveRequest = (draft: DepDraft, rowVersion: number): SaveDepCalcRequest => ({
  roundingPrecision: draft.roundingPrecision,
  taxRate: draft.taxRate,
  bookProrataConvention: draft.bookProrataConvention,
  dtOpeningPosition: draft.dtOpeningPosition,
  dtClosingPositionActual: draft.dtClosingPositionActual,
  dtClosingPositionRounded: draft.dtClosingPositionRounded,
  dtChargeForYearActual: draft.dtChargeForYearActual,
  dtChargeForYearRounded: draft.dtChargeForYearRounded,
  classConfig: draft.classConfig,
  additions: draft.additions.map((a, i) => ({
    scheduleId: a.scheduleId,
    scheduleClassId: a.scheduleClassId,
    customClassLocalId: a.customClassLocalId,
    subClassLocalId: a.subClassLocalId,
    ledgerName: a.ledgerName,
    description: a.description,
    additionDate: a.additionDate,
    putToUseDate: a.putToUseDate,
    amount: a.amount,
    itBlockEnumId: a.itBlockEnumId,
    itRateOverride: a.itRateOverride,
    displayOrder: i,
    daysUsed: a.daysUsed,
    isLt180Days: a.isLt180Days,
    bookDepActual: a.bookDepActual,
    bookDepRounded: a.bookDepRounded,
    itDepIndicativeActual: a.itDepIndicativeActual,
    itDepIndicativeRounded: a.itDepIndicativeRounded,
  })),
  disposals: draft.disposals.map((d, i) => ({
    scheduleId: d.scheduleId,
    scheduleClassId: d.scheduleClassId,
    customClassLocalId: d.customClassLocalId,
    subClassLocalId: d.subClassLocalId,
    ledgerName: d.ledgerName,
    description: d.description,
    originalCost: d.originalCost,
    accumulatedDepreciation: d.accumulatedDepreciation,
    disposalDate: d.disposalDate,
    disposalProceeds: d.disposalProceeds,
    itBlockEnumId: d.itBlockEnumId,
    displayOrder: i,
    netBookValue: d.netBookValue,
    profitLossOnDisposal: d.profitLossOnDisposal,
  })),
  itBlocks: draft.itBlocks.map(b => ({
    itBlockEnumId: b.itBlockEnumId,
    openingWdv: b.openingWdv,
    rate: b.rate,
    additionsGe180: b.additionsGe180,
    additionsLt180: b.additionsLt180,
    disposalProceeds: b.disposalProceeds,
    itDepActual: b.itDepActual,
    itDepRounded: b.itDepRounded,
    closingWdvActual: b.closingWdvActual,
    closingWdvRounded: b.closingWdvRounded,
    isBlockNegative: b.isBlockNegative,
    isMarkedExhausted: b.isMarkedExhausted,
    itProfitLoss: b.itProfitLoss,
  })),
  rowVersion,
});
